import { useMemo } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";

// react-chartjs-2 components
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const dayLabel = (isoDate) => {
  try {
    return format(new Date(isoDate), "d MMM");
  } catch {
    return isoDate;
  }
};

function buildScales() {
  return {
    y: {
      display: true,
      grid: { display: true, drawBorder: false, drawTicks: false, color: "#e3e6ee" },
      ticks: { display: true, font: { size: 10 } },
    },
    x: {
      display: true,
      grid: { display: false, drawBorder: false, drawTicks: false },
      ticks: {
        display: true,
        autoSkip: false,
        maxRotation: 60,
        minRotation: 45,
        font: { size: 10 },
        callback: function (value) {
          return dayLabel(this.getLabelForValue(value));
        },
      },
    },
  };
}

function buildLineData(labels, datasets) {
  return {
    labels,
    datasets: (datasets || []).map((d) => ({
      ...d,
      tension: 0.35,
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointBackgroundColor: d.borderColor,
      fill: true,
      backgroundColor: `${d.borderColor}1f`,
    })),
  };
}

function buildBarData(labels, datasets) {
  return {
    labels,
    datasets: (datasets || []).map((d) => ({
      ...d,
      backgroundColor: d.borderColor,
      borderRadius: 3,
      maxBarThickness: 22,
    })),
  };
}

function chartOptions(showLegend) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: "top",
        align: "end",
        labels: { boxWidth: 8, boxHeight: 8, usePointStyle: true, font: { size: 11 } },
      },
      tooltip: { mode: "index", intersect: false },
    },
    interaction: { intersect: false, mode: "index" },
    scales: buildScales(),
  };
}

function TransactionsTrendCard({ dateRangeLabel, countChart, amountChart }) {
  const showLegend = (countChart.datasets || []).length > 1;

  const lineData = useMemo(
    () => buildLineData(countChart.labels, countChart.datasets),
    [countChart]
  );
  const barData = useMemo(
    () => buildBarData(amountChart.labels, amountChart.datasets),
    [amountChart]
  );
  const lineOptions = useMemo(() => chartOptions(showLegend), [showLegend]);
  const barOptions = useMemo(() => chartOptions(showLegend), [showLegend]);

  return (
    <Card
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.borders.borderRadius.xl,
        boxShadow: theme.boxShadows.md,
        overflow: "hidden",
      })}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2.25}
        py={2}
        sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}` })}
      >
        <MDTypography variant="h6" fontWeight="medium">
          Transactions trend
        </MDTypography>
        <MDTypography variant="caption" color="secondary">
          {dateRangeLabel}
        </MDTypography>
      </MDBox>
      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          sx={(theme) => ({
            borderRight: { md: `1px solid ${theme.palette.grey[300]}` },
            borderBottom: { xs: `1px solid ${theme.palette.grey[300]}`, md: "none" },
          })}
        >
          <MDBox p={2.5}>
            <MDTypography variant="caption" fontWeight="bold" color="secondary" textTransform="uppercase">
              Transaction count
            </MDTypography>
            <MDBox height="240px" mt={1.5}>
              <Line data={lineData} options={lineOptions} redraw />
            </MDBox>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox p={2.5}>
            <MDTypography variant="caption" fontWeight="bold" color="secondary" textTransform="uppercase">
              Transaction value
            </MDTypography>
            <MDBox height="240px" mt={1.5}>
              <Bar data={barData} options={barOptions} redraw />
            </MDBox>
          </MDBox>
        </Grid>
      </Grid>
    </Card>
  );
}

TransactionsTrendCard.defaultProps = {
  dateRangeLabel: "",
};

TransactionsTrendCard.propTypes = {
  dateRangeLabel: PropTypes.string,
  countChart: PropTypes.shape({
    labels: PropTypes.array,
    datasets: PropTypes.array,
  }).isRequired,
  amountChart: PropTypes.shape({
    labels: PropTypes.array,
    datasets: PropTypes.array,
  }).isRequired,
};

export default TransactionsTrendCard;
